<template>
  <div>
    <div v-if="loading" class="overlay">
      <clip-loader />
    </div>
    <div class="user-settings-header">
      <div class="avatar-wrapper" data-test="user-avatar">
        <div class="circle">
          <img
            v-if="avatar.image"
            :src="avatar.image"
            alt="Avatar"
            class="avatar-img"
          />
          <span v-else class="avatar-initials">{{ user.initials }}</span>
        </div>
        <div class="user-info">
          <avatar-cropper
            v-model="showCropper"
            upload-url="/api/internal/auth/upload-avatar"
            :request-options="{
              method: 'POST',
              credentials: 'same-origin',
              headers: avatar.headerSettings,
            }"
            :labels="avatar.buttonLabels"
            @uploaded="handleUploaded"
          />
          <h4>{{ user.fullName }}</h4>
          <p class="text-muted">
            {{ user.title }}
          </p>
        </div>
        <div class="edit-btn">
          <div class="dropdown">
            <button
              class="form-image-upload__edit btn btn-grey"
              data-toggle="dropdown"
              data-test="user-avatar-edit"
              aria-haspopup="true"
              aria-expanded="false"
              type="button"
            >
              <i class="fas fa-pen editIcon" />
              <span>{{ trans("users.edit") }}</span>
            </button>
            <div class="dropdown-menu">
              <button
                class="dropdown-item"
                type="button"
                @click="handleEditClick"
              >
                {{ trans("users.uploadLogo") }}
              </button>
              <button
                v-if="avatar.image"
                class="dropdown-item"
                type="button"
                @click="handleClearClick"
              >
                {{ trans("users.removeLogo") }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="activeTab !== 'twofa' && activeTab !== 'phrase-library'"
        class="text-right user-settings-button"
      >
        <Button
          :disabled="!isValid || loading"
          :title="isValid ? '' : trans('examination.requiredFieldMissing')"
          type="button"
          :is-loading="isSaving"
          data-test="user-settings-save"
          @click="saveFormData"
        >
          {{ trans("users.saveButton") }}
        </Button>
      </div>
    </div>

    <div class="m-t-10" :class="{ 'm-t-custom': activeTab === 'twofa' }">
      <user-header
        v-model:active-tab="activeTab"
        component-purpose="userSettings"
      />

      <div v-if="!loading" class="tab-content">
        <div
          id="profile"
          :class="{ 'tab-pane': true, active: activeTab === 'profile' }"
        >
          <user-profile own-profile="true" :user="user" :errors="errors" />
          <div class="text-right mb-5">
            <button
              class="btn btn-primary"
              data-test="profile-save"
              :disabled="!isValid || loading"
              :title="isValid ? '' : trans('examination.requiredFieldMissing')"
              @click="saveFormData"
            >
              {{ trans("users.saveButton") }}
            </button>
          </div>
        </div>

        <div
          id="settings"
          :class="{ 'tab-pane': true, active: activeTab === 'settings' }"
        >
          <div class="row">
            <div class="col-md-12">
              <user-settings
                :user="user"
                :centers="centers"
                :primary-center="primaryCenter"
                :external-resources="externalResources"
                :errors="errors"
                :user-roles="userRoles"
                :current-role="dropdownRole(user)"
              />
            </div>
          </div>
        </div>

        <div
          id="products"
          :class="{ 'tab-pane': true, active: activeTab === 'products' }"
        >
          <user-products
            ref="userProducts"
            user-id=""
            source="settings"
            context=""
            :can-view-clinical-data="canViewClinicalData"
          />
        </div>

        <div
          id="twofa"
          :class="{ 'tab-pane': true, active: activeTab === 'twofa' }"
        >
          <two-factor-authentication
            v-if="activeTab === 'twofa'"
            :user="user"
            @verification-success="fetchUserOnVerificationSuccess"
          />
        </div>

        <div
          id="history"
          :class="{ 'tab-pane': true, active: activeTab === 'history' }"
        >
          <user-history
            :timeline="timeline"
            @get-more-notifications="getMoreNotifications"
          />
        </div>
        <div
          id="phrase-library"
          :class="{ 'tab-pane': true, active: activeTab === 'phrase-library' }"
        >
          <user-phrase-library :user="user" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AvatarCropper from "vue-avatar-cropper";

import TwoFactorAuthentication from "./TwoFactorAuthentication.vue";
import UserHeader from "./UserHeader.vue";
import UserHistory from "./UserHistory.vue";
import UserProfile from "../Admin/Users/User/UserProfile.vue";
import UserProducts from "../Admin/Users/User/UserProducts.vue";
import UserSettings from "./UserSettings.vue";
import UserRolesMixin from "../../mixins/UserRolesMixin.vue";
import { lang } from "../../i18n";
import { getXsrfToken } from "../../app/practitioner-portal/auth/xsrf-token";
import { initCsrf } from "../../app/practitioner-portal/auth/csrf";

import Button from "../Layout/Button.vue";
import UserPhraseLibrary from "../Admin/Users/User/UserPhraseLibrary/UserPhraseLibrary.vue";

export default {
  components: {
    UserPhraseLibrary,
    AvatarCropper,
    TwoFactorAuthentication,
    UserHeader,
    UserHistory,
    UserProfile,
    UserProducts,
    UserSettings,
    Button,
  },
  mixins: [UserRolesMixin],
  data() {
    return {
      activeTab: "profile",
      errors: [],
      loading: false,
      isSaving: false,
      user: "",
      primaryCenterId: null,
      primaryCenter: {
        id: null,
      },
      centers: "",
      externalResources: "",
      products: {
        notifications_new_examinations: false,
        consensus_notifications: false,
        notification_new_document_consult_answer: false,
        unanswered_cases: false,
        cases_without_photos: false,
      },
      timeline: {
        data: {},
        paginate: {},
      },
      showCropper: false,
      avatar: {
        image: undefined,
        headerSettings: {},
        buttonLabels: { submit: "Submit", cancel: "Cancel" },
        cropperOptions: {
          autoCropArea: 1,
          viewMode: 1,
          movable: 1,
          zoomable: 1,
        },
      },
      roles: [],
      hasAnyExternalResourceRole: false,
      userRoles: {
        consultant: false,
        senior_consultant: false,
        user: false,
        user_without_clinical_view: false,
        center_admin: false,
        region_admin: false,
        external_resource: false,
        region_external_resource: false,
        admin: false,
        case_admin: false,
      },
      currentRoles: {
        consultant: false,
        senior_consultant: false,
        user: false,
        user_without_clinical_view: false,
        center_admin: false,
        region_admin: false,
        external_resource: false,
        region_external_resource: false,
        admin: false,
        case_admin: false,
      },
    };
  },
  computed: {
    canViewClinicalData() {
      return !this.userRoles.user_without_clinical_view;
    },
    isValid: function () {
      if (
        !this.user.culture ||
        !this.user.username ||
        !this.user.email ||
        !this.user.firstname ||
        !this.user.lastname
      ) {
        return false;
      } else {
        return true;
      }
    },
  },
  created() {
    this.loadData();
  },
  mounted() {
    // Set the initial active tab based on the URL hash, if present
    const tab = this.$route.params.tab;
    const validTabs = [
      "profile",
      "settings",
      "products",
      "twofa",
      "history",
      "phrase-library",
    ];
    if (validTabs.includes(tab)) {
      this.activeTab = tab;
    }
  },
  methods: {
    loadData() {
      this.loading = true;
      axios
        .get("/api/internal/auth/settings")
        .then((data) => {
          let collection = data.data;
          this.user = collection.user;
          this.primaryCenter = collection.primaryCenter;

          this.centers = collection.centers;
          this.externalResources = collection.externalResources;
          this.products = collection.user.settings_products;
          this.timeline = collection.notifications;

          this.avatar.image = this.$store.state.user.avatar;

          // assign role to true if the role is in the assigned user roles list
          this.userRoles = this.mapRolesToBoolean(this.user.roles);
          this.currentRoles = this.mapRolesToBoolean(this.user.roles);

          this.loading = false;
        })
        .catch((error) => {
          this.userErrorMessage(error);
          this.loading = false;
        });
    },
    fetchUserOnVerificationSuccess() {
      axios
        .get("/api/internal/auth/settings")
        .then((data) => {
          let collection = data.data;
          this.user = collection.user;
        })
        .catch((error) => {
          this.userErrorMessage(error);
        });
    },
    saveFormData(e) {
      e.preventDefault();
      /**
       * We want to show a dialog to confirm that you are deactivating a user.
       * We can check this by seeing if the current user is not blocked, and the new
       * pendingRoles.user value is false.
       *
       * The direct mutation of props is very confusing and error-prone here, as well as
       * the duplication of the various roles/pendingRoles, so this seems like the easiest way to
       * handle this for now.
       */

      let noPendingExternalResourceRole =
        !this.userRoles.external_resource &&
        !this.userRoles.region_external_resource;

      let message = [
        noPendingExternalResourceRole && this.currentRoles.external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalExternalResourceRemoveMessage",
            )
          : false,
        noPendingExternalResourceRole &&
        this.currentRoles.region_external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalRegionExternalResourceRemoveMessage",
            )
          : false,
      ].filter(Boolean);

      if (message.length > 0) {
        this.$vueAlert
          .confirm(undefined, undefined, undefined, {
            html: message.map((msg) => {
              return msg;
            })[0],
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
          })
          .then(() => {
            // If confirmed, proceed with saving.
            this.submitForm();
          })
          .catch(() => {
            // If canceled, abort the save.
            return false;
          });
      } else {
        this.submitForm();
      }
    },

    submitForm() {
      this.errors = [];

      this.user.products = this.products;
      this.user.userRoles = this.userRoles;
      this.user.settings_products = this.$refs.userProducts.getProducts();

      this.isSaving = true;

      axios
        .post("/api/internal/auth/settings", this.user)
        .then((response) => {
          if (response.data.success) {
            this.isSaving = false;
            this.$notify({
              text: lang.get("general.notificationSaved"),
              type: "success",
              duration: 1000,
            });
            this.currentRoles = Object.assign({}, this.userRoles);
            // With this form, it's possible to change the preferred locale.
            lang.setLocale(this.user.culture);
          }
        })
        .catch((error) => {
          this.isSaving = false;
          this.errors = error.response.data;
          this.userErrorMessage(error);
        });
    },
    async handleEditClick() {
      await initCsrf();
      this.avatar.headerSettings = { "X-XSRF-TOKEN": getXsrfToken() };
      this.showCropper = true;
    },
    handleClearClick() {
      axios
        .delete("/api/internal/auth/delete-avatar")
        .then((response) => {
          this.avatar.image = response.avatar;
          this.$store.commit("setUserAvatar", this.avatar.image);

          this.$notify({
            text: lang.get("general.notificationSaved"),
            type: "success",
            duration: 1000,
          });
        })
        .catch((error) => {
          this.errors = error.response.data;
          this.userErrorMessage(error);
        });
    },
    handleUploaded({ response }) {
      response.json().then((data) => {
        this.avatar.image = data.avatar;
        this.$store.commit("setUserAvatar", this.avatar.image);

        this.$notify({
          text: lang.get("general.notificationSaved"),
          type: "success",
          duration: 1000,
        });
      });
    },
    getMoreNotifications: function () {
      if (this.timeline.paginate.next_page_url) {
        axios
          .get(this.timeline.paginate.next_page_url)
          .then((response) => {
            let collection = response.data;

            if (collection) {
              for (var i = 0; i < collection.data.length; i++) {
                this.timeline.data.push(collection.data[i]);
              }

              this.timeline.paginate = collection.paginate;
            }
          })
          .catch((error) => {
            this.userErrorMessage(error);
          });
      }
    },
    mapRolesToBoolean(roles) {
      return roles.reduce((acc, role) => {
        acc[role.name] = true;
        return acc;
      }, {});
    },
    getExternalResourceModalTranslation(translationKey) {
      return this.trans(translationKey, {
        userGlue: this.trans("general.modalExternalResourceRemoveUserGlue"),
        theirGlue: this.trans("general.modalExternalResourceRemoveTheirGlue"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../sass/components/custom_user_avatar.scss";

.form-image-upload__edit {
  position: absolute;
  bottom: -1em;
  left: 50%;
  transform: translateX(-50%);
}
$custom-margin-top: 10px;

.m-t-custom {
  margin-top: $custom-margin-top !important;
}
</style>
